import { createSelector, createSelectorCreator, defaultMemoize } from 'reselect';
import { ProductType, RulesName, RulesType } from '../../enums';

import { sortSubscriptionsByDate } from '../../utils/parsers';
import { applyRule, verifyProductsRules } from '../../utils/rules';
import {
  selectAddOnSubscriptions,
  selectProductTypeAddOn,
  selectProductsEntities,
  selectSubscriptionsEntities,
  selectClientProducts,
  selectProductTypePlan
} from './bootstrap';
import { selectCurrentPaymentMethod } from './payment';

import { getEntities } from '../../global-selectors';
import { get } from 'lodash';


// This selector is recalculated every time the component is entered in React.
// Check when it is necessary to use it by performance.
const createNoMemoSelector = createSelectorCreator(defaultMemoize, () => false);

export const selectAccountProducts = createSelector(
  selectClientProducts,
  productList => productList
);

export const selectProducts = createSelector(
  selectProductsEntities,
  productList => productList.sort((a, b) => a.order - b.order).filter(product => product.productID !== ProductType.ACCESS_PLAN_DEFAULT_PRODUCTID)
);

export const selectProductsPlan = createSelector(
  selectProductTypePlan,
  plans => plans.sort((a, b) => a.order - b.order)
);

export const selectAddOns = createSelector(
  selectProductTypeAddOn,
  selectProductsEntities,
  (addOns, products) => addOns.map((pkg) => {
    return products && products.find(productInf => productInf.productID === pkg.productID);
  })
);

export const selectActivePlanSubscription = createNoMemoSelector(
  selectSubscriptionsEntities,
  (subscriptions) => {
    return get(subscriptions, 'activePlan', null);
  }
);

export const selectPlanToShow = createNoMemoSelector(
  selectSubscriptionsEntities,
  (subscriptions) => {
    return subscriptions.planToShow;
  }
);

export const selectActivePlanPaymentData = createNoMemoSelector(
  selectActivePlanSubscription,
  selectProductsEntities,
  selectCurrentPaymentMethod,
  (plan, products, paymentMethod) => {
    const planInfo = products.find(product => product.productID === plan?.productId) || {};
    return planInfo.paymentData?.find(data => data.paymentMethod === paymentMethod);
  }
);

export const selectAvailablePlansToChange = createNoMemoSelector(
  selectActivePlanSubscription,
  selectProductTypePlan,
  (userPlanSubscription, plans) => {
    const availablePlans = plans
      .sort((a, b) => a.order - b.order);
    return userPlanSubscription && availablePlans;
  }
);

export const selectActiveAddOnSubscriptions = createSelector(
  selectAddOnSubscriptions,
  (addOnSubscriptions) => {
    return addOnSubscriptions.length > 0
      ? addOnSubscriptions
      : []
  }
);

export const selectAvailableAddOns = createSelector(
  selectAddOns,
  selectAddOnSubscriptions,
  (addOns, addOnSubscriptions) => {
    const productsIds = addOnSubscriptions.map(p => p.productId);
    const upgradeableToRule = verifyProductsRules(RulesType.ADDITIONAL_PURCHASE, RulesName.UPGRADEABLE_TO, productsIds, addOns);
    const upgradeableToList = upgradeableToRule.length > 0 && applyRule(RulesName.UPGRADEABLE_TO, upgradeableToRule, productsIds, addOns);
    return upgradeableToList || addOns.filter(pkg => !(addOnSubscriptions.some(subscription => subscription.productId === pkg.productID)));
  }
);

const paymentMethodsFromApps = ['googleplaybilling', 'applestorekit', 'amazoniap'];

export const selectIsSubscriptionFromApps = createSelector(
  selectActivePlanSubscription,
  planSubscription => {
    return paymentMethodsFromApps.includes(planSubscription?.purchaseState?.paymentMethod);
  }
);

export const selectSubscriptionMethodId = createSelector(
  selectActivePlanSubscription,
  planSubscription => {
    return planSubscription?.purchaseState?.paymentMethod;
  }
);

export const getSelectedFreePassProduct = createSelector(
  getEntities,
  entities => get(entities, 'selectedFreePassProduct', null)
);